
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { getStoredChildren, storeQuotes, QuoteItem } from '@/utils/storage';
import { logger } from '@/utils/logger';

interface QuoteDialogProps {
  children: React.ReactNode;
  onQuoteAdded?: (quote: QuoteItem) => void;
  defaultChildId?: string;
}

const QuoteDialog = ({ children, onQuoteAdded, defaultChildId }: QuoteDialogProps) => {
  const [open, setOpen] = useState(false);
  const [childrenData, setChildrenData] = useState<{ id: string; name: string }[]>([]);
  const [newQuote, setNewQuote] = useState<Omit<QuoteItem, 'id' | 'childName'>>({
    content: '',
    childId: defaultChildId || '',
    date: new Date().toISOString().split('T')[0],
    context: ''
  });

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const storedChildren = await getStoredChildren();
        if (storedChildren.length > 0) {
          setChildrenData(storedChildren.map(child => ({ id: child.id, name: child.name })));
        }
      } catch (error) {
        logger.error("Error fetching children:", error);
      }
    };
    
    fetchChildren();
  }, []);

  // Set defaultChildId when it changes
  useEffect(() => {
    if (defaultChildId) {
      setNewQuote(prev => ({
        ...prev,
        childId: defaultChildId
      }));
    }
  }, [defaultChildId]);

  const resetForm = () => {
    setNewQuote({
      content: '',
      childId: defaultChildId || '',
      date: new Date().toISOString().split('T')[0],
      context: ''
    });
  };

  const handleAddQuote = async () => {
    if (!newQuote.content || !newQuote.childId || !newQuote.date) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const newQuoteItem = await storeQuotes(newQuote);
      
      if (newQuoteItem && onQuoteAdded) {
        onQuoteAdded(newQuoteItem);
      }
      
      resetForm();
      setOpen(false);
    } catch (error) {
      logger.error("Error adding quote:", error);
      toast({
        title: "Error",
        description: "Failed to add quote. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] w-[95%] max-h-[90vh] overflow-y-auto rounded-lg">
        <DialogHeader>
          <DialogTitle>Add New Quote</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="child">Child</Label>
            <Select 
              onValueChange={(value) => setNewQuote({...newQuote, childId: value})}
              value={newQuote.childId}
            >
              <SelectTrigger className="rounded-md">
                <SelectValue placeholder="Select a child" />
              </SelectTrigger>
              <SelectContent>
                {childrenData.map(child => (
                  <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="quote">Quote</Label>
            <Textarea 
              id="quote" 
              placeholder="What did they say?"
              value={newQuote.content}
              onChange={(e) => setNewQuote({...newQuote, content: e.target.value})}
              className="min-h-[80px] rounded-md max-h-[150px] overflow-y-auto"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="date">Date</Label>
            <Input 
              id="date" 
              type="date" 
              value={newQuote.date}
              onChange={(e) => setNewQuote({...newQuote, date: e.target.value})}
              className="rounded-md"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="context">Context (optional)</Label>
            <Textarea 
              id="context" 
              placeholder="What was happening when they said this?"
              value={newQuote.context || ''}
              onChange={(e) => setNewQuote({...newQuote, context: e.target.value})}
              className="rounded-md max-h-[100px] overflow-y-auto"
            />
          </div>
          <Button onClick={handleAddQuote} className="mt-2 rounded-full">Save Quote</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDialog;
